import { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Info, Lightbulb, Sparkles, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FeatureHelpContent } from '../../data/featureHelp';

/**
 * A page's help in the person's language (medopd-docs 97): `help:<pageKey>` holds `title`, `summary`, `who`,
 * `points` and `tips` (numbered maps, rendered in order) and `fields` (one line per field, shown by HelpTip).
 * Returns null when the page has no entry.
 */
export function usePageHelp(pageKey: string | undefined): FeatureHelpContent | null {
  const { t, i18n } = useTranslation('help');
  if (!pageKey || !i18n.exists(`${pageKey}.summary`, { ns: 'help' })) return null;
  const list = (k: string): string[] => {
    const v = t(`${pageKey}.${k}`, { returnObjects: true, defaultValue: {} }) as unknown;
    if (!v || typeof v !== 'object') return [];
    return Object.keys(v as Record<string, string>)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => (v as Record<string, string>)[key])
      .filter((s) => typeof s === 'string' && s.length > 0);
  };
  return {
    title: t(`${pageKey}.title`),
    summary: t(`${pageKey}.summary`),
    whatHappens: list('points'),
    whoItsFor: i18n.exists(`${pageKey}.who`, { ns: 'help' }) ? t(`${pageKey}.who`) : undefined,
    tips: list('tips'),
  };
}

/** The "about this page" dialog — shared by the floating Learn more button and the (i) beside page titles. */
export function PageHelpDialog({ content, open, onClose }: { content: FeatureHelpContent; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('ui');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.1)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Info size={17} />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>{content.title}</Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label={t('close', { defaultValue: 'Close' })}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{content.summary}</Typography>

          {content.whatHappens.length > 0 && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <Sparkles size={14} color="#1976d2" />
                <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}>
                  {t('whatHappensHere')}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {content.whatHappens.map((item, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.9, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {content.whoItsFor && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Users size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">{content.whoItsFor}</Typography>
            </Stack>
          )}

          {content.tips && content.tips.length > 0 && (
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.06)' }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <Lightbulb size={14} color="#ed6c02" />
                <Typography variant="caption" fontWeight={700} color="warning.dark">{t('goodToKnow')}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                {content.tips.map((tip, i) => (
                  <Typography key={i} variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{tip}</Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

/** An (i) beside a page title that opens that page's help. Renders nothing if the page has no help yet. */
export function PageHelpButton({ pageKey, size = 18 }: { pageKey: string; size?: number }) {
  const { t } = useTranslation('ui');
  const content = usePageHelp(pageKey);
  const [open, setOpen] = useState(false);
  if (!content) return null;
  return (
    <>
      <Tooltip title={t('learnMoreAboutThisPage')}>
        <IconButton size="small" onClick={() => setOpen(true)} aria-label={t('learnMoreAboutThisPage')}
          sx={{ ml: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
          <Info size={size} />
        </IconButton>
      </Tooltip>
      <PageHelpDialog content={content} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
